import { Injectable } from '@angular/core';
import { StorageService } from '../storage-service.service';
import { Locale } from 'date-fns';
import { it, enUS, fr, nl, de } from 'date-fns/locale';
import { Subject } from 'rxjs';
import TESTI_IT from 'src/assets/i18n/IT.json';

@Injectable({ providedIn: 'root' })
export class LangProvider {

  languageSelected = "IT"; // lingua default, viene sostituito dal codice della lingua selezionata
  flagLanguageSelected = "It"; // lingua delle bandierine da far vedere a video

  // Getter per il locale code da usare nei componenti ion-datetime
  get localeCode(): string {
    switch (this.languageSelected?.toUpperCase()) {
      case 'EN':
        return 'en-US';
      case 'FR':
        return 'fr-FR';
      case 'NL':
        return 'nl-NL';
      case 'DE':
        return 'de-DE';
      case 'IT':
      default:
        return 'it-IT';
    }
  }

  // Getter per il locale di date-fns da usare nelle funzioni format()
  get dateFnsLocale(): Locale {
    switch (this.languageSelected?.toUpperCase()) {
      case 'EN':
        return enUS;
      case 'FR':
        return fr;
      case 'NL':
        return nl;
      case 'DE':
        return de;
      case 'IT':
      default:
        return it;
    }
  }

  // L'italiano e' nel bundle: c'e' subito all'avvio e resta come riserva se il JSON di un'altra lingua non arriva
  // (nella PWA i file vengono dalla rete). Senza, comparirebbero i nomi delle chiavi.
  private translations: Record<string, string> = TESTI_IT;
  translationsLoaded: Promise<void>;

  // Cambio lingua senza ricaricare: chi mostra contenuti del server che dipendono dalla lingua (meteo, barra
  // informativa) si iscrive e li rilegge; i testi con il pipe tr si aggiornano da soli
  private linguaCambiata = new Subject<string>();
  readonly linguaCambiata$ = this.linguaCambiata.asObservable();
  private ultimaRichiesta = 0;

  constructor(private storage: StorageService) {
    this.translationsLoaded = this.storage.get('locale_lang').then(async (val) => {
      const lang = (val && val !== '') ? val : 'IT';
      if (!val || val === '') {
        this.storage.set('locale_lang', 'IT');
      }
      const testi = await this.loadTranslations(lang);
      this.languageSelected = testi ? lang : 'IT';
      this.translations = testi ?? TESTI_IT;
      this.controllaFlag();
      this.aggiornaLangPagina();
    });
  }

  // null se il file non arriva (rete assente, risposta non ok o non JSON)
  private async loadTranslations(lang: string): Promise<Record<string, string> | null> {
    const codice = lang.toUpperCase();
    if (codice === 'IT') {
      return TESTI_IT;
    }
    try {
      const response = await fetch(`assets/i18n/${codice}.json`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return await response.json();
    } catch (e) {
      console.error('Error loading translations for', lang, e);
      return null;
    }
  }

  // lettori di schermo e sillabazione seguono la lingua dei testi
  private aggiornaLangPagina() {
    document.documentElement.lang = this.languageSelected.toLowerCase();
  }

  getTranslation(key: string): string | undefined {
    return this.translations[key] ?? TESTI_IT[key];
  }

  controllaFlag() {
    if (this.languageSelected == 'EN' || this.languageSelected == 'en' || this.languageSelected == 'En') {
      this.flagLanguageSelected = 'En';
    } else if (this.languageSelected == 'IT' || this.languageSelected == 'it' || this.languageSelected == 'It') {
      this.flagLanguageSelected = 'It';
    } else if (this.languageSelected == 'FR' || this.languageSelected == 'fr' || this.languageSelected == 'Fr') {
      this.flagLanguageSelected = 'Fr';
    } else if (this.languageSelected == 'NL' || this.languageSelected == 'nl' || this.languageSelected == 'Nl') {
      this.flagLanguageSelected = 'Nl';
    } else if (this.languageSelected == 'DE' || this.languageSelected == 'de' || this.languageSelected == 'De') {
      this.flagLanguageSelected = 'De';
    }
  }

  /**
   * Cambia la lingua senza ricaricare la WebView (la ricarica riavviava l'app e, su Android, poteva bloccare lo
   * splash). Con due scelte ravvicinate vince l'ultima, anche se il suo JSON arriva prima.
   * Se il JSON non arriva (PWA senza rete) resta la lingua attuale, anche quella salvata.
   * @param lingua - Codice della lingua (IT, EN, FR, NL, DE), anche nella forma "Fr" dei pulsanti della welcome
   * @returns false solo se i testi della lingua scelta non si sono potuti caricare
   */
  async chooseLanguage(lingua: string): Promise<boolean> {
    const nuova = (lingua + '').toUpperCase();
    if (nuova === this.languageSelected?.toUpperCase()) {
      return true;
    }
    const richiesta = ++this.ultimaRichiesta;
    const testi = await this.loadTranslations(nuova);
    if (richiesta !== this.ultimaRichiesta) {
      return true;
    }
    if (!testi) {
      return false;
    }
    this.translations = testi;
    this.languageSelected = nuova;
    this.controllaFlag();
    this.aggiornaLangPagina();
    this.linguaCambiata.next(nuova);
    await this.storage.set('locale_lang', nuova);
    return true;
  }

  /**
   * Metodo helper per ottenere una label in base alla lingua selezionata
   * @param baseName - Nome base della label senza suffisso lingua
   * @returns La stringa tradotta nella lingua corrente
   */
  private getLabel(baseName: string): string {
    return this.getTranslation(baseName) || '';
  }


  // Placeholders
  get placeholder_inserisciEmail(): string { return this.getLabel('placeholder_inserisciEmail'); }
  get placeholder_inserisciPassword(): string { return this.getLabel('placeholder_inserisciPassword'); }
  get placeholder_inserisciNome(): string { return this.getLabel('placeholder_inserisciNome'); }
  get placeholder_inserisciCognome(): string { return this.getLabel('placeholder_inserisciCognome'); }
  get placeholder_inserisciNumero(): string { return this.getLabel('placeholder_inserisciNumero'); }
  get placeholder_inserisciNuovaPassword(): string { return this.getLabel('placeholder_inserisciNuovaPassword'); }
  get placeholder_confermaPassword(): string { return this.getLabel('placeholder_confermaPassword'); }
  get placeholder_selezionaCoperti(): string { return this.getLabel('placeholder_selezionaCoperti'); }
  get placeholder_notePrenotazione(): string { return this.getLabel('placeholder_notePrenotazione'); }
  get placeholder_cerca(): string { return this.getLabel('placeholder_cerca'); }
  get placeholder_nomeField(): string { return this.getLabel('placeholder_nomeField'); }
  get placeholder_cognomeField(): string { return this.getLabel('placeholder_cognomeField'); }
  get placeholder_telefonoField(): string { return this.getLabel('placeholder_telefonoField'); }
  get placeholder_emailField(): string { return this.getLabel('placeholder_emailField'); }
  get placeholder_orario(): string { return this.getLabel('placeholder_orario'); }

  // Sezioni
  get sezione_informazioniPersonali(): string { return this.getLabel('sezione_informazioniPersonali'); }
  get sezione_contatti(): string { return this.getLabel('sezione_contatti'); }
  get sezione_sicurezza(): string { return this.getLabel('sezione_sicurezza'); }
  get sezione_privacy(): string { return this.getLabel('sezione_privacy'); }

  // Login
  get label_email(): string { return this.getLabel('label_email'); }
  get label_password(): string { return this.getLabel('label_password'); }
  get bottone_login(): string { return this.getLabel('bottone_login'); }

  // Ristorante
  get ristorante_dataPrenotazione(): string { return this.getLabel('ristorante_dataPrenotazione'); }
  get ristorante_prenotaAltraData(): string { return this.getLabel('ristorante_prenotaAltraData'); }
  get ristorante_numeroCoperti(): string { return this.getLabel('ristorante_numeroCoperti'); }
  get ristorante_sceltaFasciaOraria(): string { return this.getLabel('ristorante_sceltaFasciaOraria'); }
  get ristorante_orariDisponibili(): string { return this.getLabel('ristorante_orariDisponibili'); }
  get ristorante_disponibile(): string { return this.getLabel('ristorante_disponibile'); }
  get ristorante_nonDisponibile(): string { return this.getLabel('ristorante_nonDisponibile'); }
  get ristorante_maxCoperti(): string { return this.getLabel('ristorante_maxCoperti'); }
  get ristorante_coperti(): string { return this.getLabel('ristorante_coperti'); }
  get ristorante_selezionaFascia(): string { return this.getLabel('ristorante_selezionaFascia'); }
  get ristorante_scegliPranzoOCena(): string { return this.getLabel('ristorante_scegliPranzoOCena'); }
  get ristorante_nessunOrario(): string { return this.getLabel('ristorante_nessunOrario'); }
  get ristorante_ilPranzo(): string { return this.getLabel('ristorante_ilPranzo'); }
  get ristorante_laCena(): string { return this.getLabel('ristorante_laCena'); }
  get ristorante_messaggioDopo1930(): string { return this.getLabel('ristorante_messaggioDopo1930'); }
  get ristorante_messaggioDopo1200(): string { return this.getLabel('ristorante_messaggioDopo1200'); }
  get ristorante_tipoPrenotazione(): string { return this.getLabel('ristorante_tipoPrenotazione'); }
  get ristorante_noteOpzionali(): string { return this.getLabel('ristorante_noteOpzionali'); }
  get ristorante_caratteri(): string { return this.getLabel('ristorante_caratteri'); }
  get ristorante_confermaPrenotazione(): string { return this.getLabel('ristorante_confermaPrenotazione'); }
  get ristorante_persone(): string { return this.getLabel('ristorante_persone'); }

  // Tipo Pasto
  get tipoPasto_pranzo(): string { return this.getLabel('tipoPasto_pranzo'); }
  get tipoPasto_cena(): string { return this.getLabel('tipoPasto_cena'); }
  get tipoPasto_ristorantePizzeria(): string { return this.getLabel('tipoPasto_ristorantePizzeria'); }
  get tipoPasto_soloRistorante(): string { return this.getLabel('tipoPasto_soloRistorante'); }
  get tipoPasto_soloPizzeria(): string { return this.getLabel('tipoPasto_soloPizzeria'); }
  get tipoPasto_nonSpecificato(): string { return this.getLabel('tipoPasto_nonSpecificato'); }
  get tipoPasto_ristorante(): string { return this.getLabel('tipoPasto_ristorante'); }
  get tipoPasto_pizzeria(): string { return this.getLabel('tipoPasto_pizzeria'); }
  get tipoPasto_ristoranteOPizzeria(): string { return this.getLabel('tipoPasto_ristoranteOPizzeria'); }

  // Gestione Ristorante
  get gestRistorante_selezionaData(): string { return this.getLabel('gestRistorante_selezionaData'); }
  get gestRistorante_vistaPrenotazioni(): string { return this.getLabel('gestRistorante_vistaPrenotazioni'); }
  get gestRistorante_vistaConfigurazioneSlot(): string { return this.getLabel('gestRistorante_vistaConfigurazioneSlot'); }
  get gestRistorante_filtraPerTipo(): string { return this.getLabel('gestRistorante_filtraPerTipo'); }
  get gestRistorante_tutti(): string { return this.getLabel('gestRistorante_tutti'); }
  get gestRistorante_mealTitlePranzo(): string { return this.getLabel('gestRistorante_mealTitlePranzo'); }
  get gestRistorante_mealTitleCena(): string { return this.getLabel('gestRistorante_mealTitleCena'); }

  // Carrello
  get carrello_di(): string { return this.getLabel('carrello_di'); }
  get carrello_ombrelloni(): string { return this.getLabel('carrello_ombrelloni'); }
  get carrello_dal(): string { return this.getLabel('carrello_dal'); }
  get carrello_al(): string { return this.getLabel('carrello_al'); }
  get carrello_tipo(): string { return this.getLabel('carrello_tipo'); }
  get carrello_serviziInclusi(): string { return this.getLabel('carrello_serviziInclusi'); }
  get carrello_cani(): string { return this.getLabel('carrello_cani'); }
  get carrello_note(): string { return this.getLabel('carrello_note'); }

  // Scelta Ombrellone
  get ombrellone_disponibile(): string { return this.getLabel('ombrellone_disponibile'); }

  // Contatti
  get contatti_titolo(): string { return this.getLabel('contatti_titolo'); }

  // Generiche Ordine
  get ordine_generico(): string { return this.getLabel('ordine_generico'); }
  get label_ombrellone_generico(): string { return this.getLabel('label_ombrellone_generico'); }

  // Alert
  get alert_selezionaSlot(): string { return this.getLabel('alert_selezionaSlot'); }
  get alert_errorePrenotazione(): string { return this.getLabel('alert_errorePrenotazione'); }
  get alert_erroreCompletamento(): string { return this.getLabel('alert_erroreCompletamento'); }
  get alert_maxCopertiSlot(): string { return this.getLabel('alert_maxCopertiSlot'); }
  get alert_slotNonDisponibile(): string { return this.getLabel('alert_slotNonDisponibile'); }

}
